'use client'

import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowRight, ChevronDown, Globe, Play, X } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

import cryptoBackground from '~/assets/crypto_background.png'
import felixBick from '~/assets/felix_bick.png'
import financeBusinessImage from '~/assets/finance_business_meeting_scene.png'
import introCover from '~/assets/intro_cover.png'
import presentationBackground from '~/assets/presentation_background.png'
import softwareBackground from '~/assets/software_background.png'
import softwareScreenshot from '~/assets/software_screenshot.webp'

import polarTensorLogo from '~/assets/polar_tensor_logo.png'

import { useLanguage } from './language'
import { languages } from './translations'

const premiumEase = [0.16, 1, 0.3, 1]

const fadeInUp = {
  initial: { opacity: 0, y: 40, filter: 'blur(10px)' },
  animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
  transition: { duration: 1, ease: premiumEase },
}

const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`

const gradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`

// ── Root ──

const Root = styled(Box)`
  min-height: 100vh;
  background: #0a1628;
  color: white;
  overflow-x: hidden;
  font-family: sans-serif;

  & ::selection {
    background: rgba(6, 182, 212, 0.2);
    color: rgb(103, 232, 249);
  }
`

// ── Background Glows ──

const DesktopGlow = styled(Box)`
  position: fixed;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  @media (max-width: 899px) {
    display: none;
  }
`

const MobileGlow = styled(Box)`
  position: fixed;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  @media (min-width: 900px) {
    display: none;
  }
`

const DesktopGlowTop = styled(Box)`
  position: absolute;
  width: 50%;
  height: 50%;
  background: rgba(6, 182, 212, 0.1);
  border-radius: 50%;
  filter: blur(120px);
  top: -10%;
  left: -10%;
`

const DesktopGlowBottom = styled(Box)`
  position: absolute;
  width: 50%;
  height: 50%;
  background: rgba(8, 145, 178, 0.1);
  border-radius: 50%;
  filter: blur(120px);
  bottom: -10%;
  right: -10%;
`

const MobileGlowTop = styled(Box)`
  position: absolute;
  width: 80%;
  height: 80%;
  background: rgba(6, 182, 212, 0.05);
  border-radius: 50%;
  filter: blur(80px);
  top: -20%;
  left: -20%;
`

const MobileGlowBottom = styled(Box)`
  position: absolute;
  width: 80%;
  height: 80%;
  background: rgba(8, 145, 178, 0.05);
  border-radius: 50%;
  filter: blur(80px);
  bottom: -20%;
  right: -20%;
`

// ── Header ──

const Header = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: 16px;
  z-index: 100;
  @media (min-width: 600px) {
    padding: 24px;
  }
`

const HeaderInner = styled(Container)`
  max-width: 1200px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Logo = styled.img`
  height: 24px;
  width: auto;
  @media (min-width: 600px) {
    height: 32px;
  }
  @media (min-width: 900px) {
    height: 40px;
  }
`

// ── Language Switcher ──

const LangButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  background: #1a1f2e;
  border: 1px solid rgba(107, 114, 128, 0.4);
  color: rgb(209, 213, 219);
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(6, 182, 212, 0.5);
    color: white;
  }

  @media (min-width: 900px) {
    padding: 8px 12px;
    font-size: 0.875rem;
  }
`

const LangDropdown = styled.div`
  margin-top: 8px;
  width: 170px;
  max-height: 280px;
  overflow-y: auto;
  border-radius: 12px;
  background: #0d1a2d;
  border: 1px solid rgba(107, 114, 128, 0.3);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7);
  z-index: 200;

  @media (min-width: 600px) {
    width: 192px;
  }
`

const LangOption = styled.button<{ active: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  background: ${(p) => (p.active ? 'rgba(6, 182, 212, 0.15)' : 'transparent')};
  color: ${(p) => (p.active ? 'rgb(103, 232, 249)' : 'rgb(209, 213, 219)')};

  &:hover {
    background: ${(p) =>
      p.active ? 'rgba(6, 182, 212, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
    color: ${(p) => (p.active ? 'rgb(103, 232, 249)' : 'white')};
  }
`

const LangName = styled.span`
  display: none;
  @media (min-width: 600px) {
    display: inline;
  }
`

// ── Hero Section ──

const HeroSection = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 64px 16px 32px;
  min-height: 0;
  overflow: hidden;

  @media (min-width: 600px) {
    padding-top: 80px;
    padding-left: 24px;
    padding-right: 24px;
  }

  @media (min-width: 900px) {
    padding-top: 128px;
    padding-bottom: 80px;
    min-height: 100vh;
  }
`

const HeroBg = styled(Box)`
  position: absolute;
  inset: 0;
  z-index: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.35;
  }
`

const HeroBgGradient = styled(Box)`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(10, 22, 40, 0.3) 0%,
    rgba(10, 22, 40, 0.5) 50%,
    rgba(10, 22, 40, 1) 100%
  );
`

const HeroContent = styled(Box)`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 16px;
  position: relative;
  z-index: 10;

  @media (min-width: 600px) {
    padding: 0 24px;
  }
`

const HeroTitle = styled(Typography)`
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.05em;
  margin-bottom: 12px;
  color: white;
  padding: 0 8px;
  text-align: center;

  @media (min-width: 600px) {
    font-size: 1.875rem;
  }
  @media (min-width: 900px) {
    font-size: 3.75rem;
    margin-bottom: 16px;
  }
`

const GradientText = styled.span`
  background: linear-gradient(
    to right,
    rgb(103, 232, 249),
    rgb(6, 182, 212),
    rgb(103, 232, 249)
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${gradient} 3s linear infinite;
`

const HeroSubtitle = styled(Typography)`
  color: rgb(156, 163, 175);
  font-size: 0.875rem;
  text-align: center;
  padding: 0 16px;

  @media (min-width: 600px) {
    font-size: 1rem;
  }
  @media (min-width: 900px) {
    font-size: 1.25rem;
  }
`

const ShimmerBorder = styled(Box)<{ color?: string }>`
  position: relative;
  padding: 2px;
  border-radius: 12px;
  background: ${(p) =>
    p.color ||
    'linear-gradient(to right, rgba(6,182,212,0.2), rgba(6,182,212,0.6), rgba(6,182,212,0.2))'};
  background-size: 200% 100%;
  animation: ${shimmer} 3s linear infinite;

  @media (min-width: 900px) {
    border-radius: 24px;
  }
`

const VideoBox = styled(Box)`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #0d1117;
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(6, 182, 212, 0.1);

  @media (min-width: 900px) {
    border-radius: 24px;
  }
`

const VideoCover = styled(Box)`
  position: relative;
  height: 100%;
  width: 100%;
  background: #0a1628;
`

const VideoCoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
`

const VideoCoverOverlay = styled(Box)`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom right,
    rgba(10, 22, 40, 0.9) 0%,
    rgba(10, 22, 40, 0.7) 50%,
    rgba(8, 145, 178, 0.5) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
`

const PlayBtn = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgb(6, 182, 212);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 15px -3px rgba(6, 182, 212, 0.3);
  transition: transform 0.2s;
  animation: ${pulse} 2s ease-in-out infinite;

  &:hover {
    transform: scale(1.1);
  }
  &:active {
    transform: scale(0.95);
  }

  @media (min-width: 900px) {
    width: 80px;
    height: 80px;
  }
`

const CTAButton = styled(Button)`
  height: 48px;
  padding: 0 32px;
  background: rgb(6, 182, 212);
  color: white;
  font-weight: 700;
  font-size: 1rem;
  border-radius: 9999px;
  text-transform: none;
  box-shadow: 0 10px 15px -3px rgba(6, 182, 212, 0.25);
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: rgb(8, 145, 178);
  }
  &:active {
    transform: scale(0.95);
  }

  @media (min-width: 900px) {
    height: 56px;
    padding: 0 48px;
    font-size: 1.125rem;
  }
`

// ── Felix Bick Section ──

const FounderSection = styled(Box)`
  position: relative;
  padding: 48px 0;
  background: #0d1117;
  border-top: 1px solid rgba(6, 182, 212, 0.1);
  overflow: hidden;

  @media (min-width: 900px) {
    padding: 80px 0;
  }
`

const FounderGrid = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;

  @media (min-width: 900px) {
    flex-direction: row;
    gap: 48px;
  }
`

const FounderImageWrap = styled(Box)`
  width: 100%;
  flex-shrink: 0;

  @media (min-width: 900px) {
    width: 41.666%;
  }
`

const FounderImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: cover;
  border-radius: 12px;
`

const FounderTextWrap = styled(Box)`
  width: 100%;

  @media (min-width: 900px) {
    width: 58.333%;
  }
`

const FounderName = styled(Typography)`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 4px;

  @media (min-width: 600px) {
    font-size: 1.875rem;
  }
  @media (min-width: 900px) {
    font-size: 2.25rem;
  }
`

const FounderRole = styled(Typography)`
  color: rgb(103, 232, 249);
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 16px;

  @media (min-width: 900px) {
    font-size: 1rem;
    margin-bottom: 24px;
  }
`

const FounderBio = styled(Typography)`
  color: rgb(156, 163, 175);
  font-size: 0.875rem;
  line-height: 1.6;
  margin-bottom: 16px;

  @media (min-width: 900px) {
    font-size: 1rem;
  }
`

const FounderQuoteBox = styled(Box)`
  margin-top: 24px;
  border-left: 2px solid rgb(103, 232, 249);
  padding-left: 16px;
`

const FounderQuote = styled(Typography)`
  color: rgb(103, 232, 249);
  font-style: italic;
  font-size: 1rem;

  @media (min-width: 900px) {
    font-size: 1.125rem;
  }
`

// ── Video Sections (Presentation / Software) ──

const VideoSection = styled(Box)<{ bg?: string }>`
  position: relative;
  padding: 40px 0;
  background: ${(p) => p.bg || '#0a1628'};
  border-top: 1px solid rgba(107, 114, 128, 0.5);
  overflow: hidden;

  @media (min-width: 900px) {
    padding: 80px 0;
  }
`

const SectionBg = styled(Box)`
  position: absolute;
  inset: 0;
  pointer-events: none;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.4;
  }
`

const SectionBgGradient = styled(Box)<{ base?: string }>`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    ${(p) => p.base || 'rgba(10,22,40,0.6)'} 0%,
    transparent 50%,
    ${(p) => p.base || 'rgba(10,22,40,0.8)'} 100%
  );
`

const SectionContent = styled(Container)`
  max-width: 1024px;
  padding: 0 16px;
  position: relative;
  z-index: 10;

  @media (min-width: 900px) {
    padding: 0 24px;
  }
`

const SectionTitle = styled(Typography)`
  font-size: 1.25rem;
  font-weight: 700;
  color: white;
  margin-bottom: 8px;
  text-align: center;

  @media (min-width: 600px) {
    font-size: 1.5rem;
  }
  @media (min-width: 900px) {
    font-size: 2.25rem;
    margin-bottom: 12px;
  }
`

const SectionSubtitle = styled(Typography)`
  color: rgb(156, 163, 175);
  font-size: 0.875rem;
  text-align: center;

  @media (min-width: 900px) {
    font-size: 1rem;
  }
`

const VioletShimmerBorder = styled(ShimmerBorder)`
  background: linear-gradient(
    to right,
    rgba(139, 92, 246, 0.1),
    rgba(167, 139, 250, 0.3),
    rgba(139, 92, 246, 0.1)
  );
  background-size: 200% 100%;
`

const CyanShimmerBorder = styled(ShimmerBorder)`
  background: linear-gradient(
    to right,
    rgba(6, 182, 212, 0.1),
    rgba(6, 182, 212, 0.3),
    rgba(6, 182, 212, 0.1)
  );
  background-size: 200% 100%;
`

const VideoSectionBox = styled(Box)`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #1a1f2e;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);

  @media (min-width: 900px) {
    border-radius: 24px;
  }
`

const VideoSectionCover = styled(Box)`
  position: relative;
  height: 100%;
  width: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const VideoSectionOverlay = styled(Box)`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom right,
    rgba(10, 22, 40, 0.9) 0%,
    rgba(10, 22, 40, 0.7) 50%,
    rgba(10, 22, 40, 0.5) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
`

const CyanPlayBtn = styled(PlayBtn)`
  background: rgba(6, 182, 212, 0.8);
  animation: none;
`

// ── Marketing Section ──

const MarketingSection = styled(Box)`
  position: relative;
  padding: 40px 0;
  background: #0a1628;
  overflow: hidden;

  @media (min-width: 900px) {
    padding: 64px 0;
  }
`

const MarketingCard = styled(Box)`
  padding: 32px;
  border-radius: 12px;
  background: rgba(26, 31, 46, 0.8);
  border: 1px solid rgba(107, 114, 128, 0.3);
  text-align: center;

  @media (min-width: 900px) {
    padding: 48px;
    border-radius: 16px;
  }
`

const GradientButton = styled(Button)`
  height: 48px;
  padding: 0 24px;
  background: linear-gradient(to right, rgb(6, 182, 212), rgb(59, 130, 246));
  color: white;
  font-weight: 700;
  font-size: 1rem;
  border-radius: 9999px;
  text-transform: none;
  box-shadow: 0 10px 15px -3px rgba(6, 182, 212, 0.2);

  &:hover {
    background: linear-gradient(to right, rgb(8, 145, 178), rgb(37, 99, 235));
  }
  &:active {
    transform: scale(0.95);
  }

  @media (min-width: 900px) {
    height: 56px;
    padding: 0 40px;
    font-size: 1.125rem;
  }
`

// ── CTA Section ──

const CTASection = styled(Box)`
  position: relative;
  padding: 48px 0;
  background: #0a1628;
  overflow: hidden;

  @media (min-width: 900px) {
    padding: 80px 0;
  }
`

const CTACard = styled(Box)`
  padding: 24px;
  border-radius: 12px;
  background: linear-gradient(to bottom right, #1a1f2e 0%, #0d1117 100%);
  border: 1px solid rgba(6, 182, 212, 0.5);
  box-shadow:
    0 0 60px rgba(6, 182, 212, 0.4),
    0 0 120px rgba(6, 182, 212, 0.2);
  text-align: center;

  @media (min-width: 900px) {
    padding: 40px;
    border-radius: 16px;
  }
`

const CTATitle = styled(Typography)`
  font-size: 1.25rem;
  color: white;
  font-weight: 700;
  line-height: 1.6;
  margin-bottom: 24px;

  @media (min-width: 600px) {
    font-size: 1.5rem;
  }
  @media (min-width: 900px) {
    font-size: 2.25rem;
    margin-bottom: 32px;
  }
`

// ── Popup ──

const PopupOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);

  @media (min-width: 900px) {
    padding: 16px;
  }
`

const PopupContent = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 1024px;
  aspect-ratio: 16 / 9;
  background: black;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);

  @media (min-width: 900px) {
    border-radius: 16px;
  }
`

const CloseBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  color: white;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  &:active {
    transform: scale(0.95);
  }

  @media (min-width: 900px) {
    top: 16px;
    right: 16px;
    width: 40px;
    height: 40px;
  }
`

// ── Still Not Ready Section ──

const NotReadySection = styled(Box)`
  position: relative;
  padding: 48px 0;
  background: #0d1117;
  border-top: 1px solid rgba(107, 114, 128, 0.3);
  overflow: hidden;

  @media (min-width: 900px) {
    padding: 80px 0;
  }
`

const NotReadyLabel = styled(Typography)`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: rgb(103, 232, 249);
  text-transform: uppercase;
  margin-bottom: 16px;
  text-align: center;

  @media (min-width: 900px) {
    font-size: 0.875rem;
    letter-spacing: 0.2em;
    margin-bottom: 24px;
  }
`

const NotReadyTitle = styled(Typography)`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  text-align: center;
  line-height: 1.3;

  @media (min-width: 600px) {
    font-size: 1.875rem;
  }
  @media (min-width: 900px) {
    font-size: 3rem;
  }
`

// ── AI Section ──

const AISection = styled(Box)`
  position: relative;
  padding: 48px 0;
  background: #0a1628;
  border-top: 1px solid rgba(107, 114, 128, 0.3);
  overflow: hidden;

  @media (min-width: 900px) {
    padding: 80px 0;
  }
`

const AITitle = styled(Typography)`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: white;
  text-transform: uppercase;
  margin-bottom: 16px;
  text-align: center;

  @media (min-width: 900px) {
    font-size: 0.875rem;
    letter-spacing: 0.2em;
    margin-bottom: 24px;
  }
`

const AIQuote = styled(Typography)`
  font-size: 1.125rem;
  color: rgb(103, 232, 249);
  font-style: italic;
  line-height: 1.6;
  text-align: center;
  padding: 0 8px;

  @media (min-width: 600px) {
    font-size: 1.25rem;
  }
  @media (min-width: 900px) {
    font-size: 1.5rem;
  }
`

// ── Disclaimer Section ──

const DisclaimerSection = styled(Box)`
  position: relative;
  padding: 32px 0;
  background: #0d1117;
  border-top: 1px solid rgba(107, 114, 128, 0.3);
  overflow: hidden;

  @media (min-width: 900px) {
    padding: 40px 0;
  }
`

const DisclaimerText = styled(Typography)`
  font-size: 0.75rem;
  color: rgb(156, 163, 175);
  line-height: 1.6;
  text-align: center;

  @media (min-width: 900px) {
    font-size: 0.875rem;
  }
`

// ── Component ──

export default function InfoPage() {
  const [playingVideo, setPlayingVideo] = useState<string | null>(null)
  const [showRewardsPopup, setShowRewardsPopup] = useState(false)
  const [showLangMenu, setShowLangMenu] = useState(false)
  const langMenuRef = useRef<HTMLDivElement>(null)
  const { lang, setLang, t } = useLanguage()
  const pathname = usePathname()
  const username = pathname.split('/').filter(Boolean)[1] ?? null
  const signUpUrl = username ? `/sign/up/${username}` : '/sign/up'

  const currentLang = languages.find((l) => l.code === lang)!

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) {
        setShowLangMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <Root>
      <DesktopGlow>
        <DesktopGlowTop />
        <DesktopGlowBottom />
      </DesktopGlow>

      <MobileGlow>
        <MobileGlowTop />
        <MobileGlowBottom />
      </MobileGlow>

      <Box sx={{ position: 'relative', zIndex: 10 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -15, filter: 'blur(10px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 1, ease: premiumEase, delay: 0.2 }}
          style={{ position: 'relative', zIndex: 100 }}
        >
          <Header>
            <HeaderInner>
              <Logo src={polarTensorLogo.src} alt='Polar Tensor' />
              <Box sx={{ position: 'relative' }} ref={langMenuRef}>
                <LangButton onClick={() => setShowLangMenu(!showLangMenu)}>
                  <Globe size={14} />
                  <span>{currentLang.flag}</span>
                  <LangName>{currentLang.name}</LangName>
                  <ChevronDown
                    size={12}
                    style={{
                      transition: 'transform 0.2s',
                      transform: showLangMenu ? 'rotate(180deg)' : 'none',
                    }}
                  />
                </LangButton>
                <AnimatePresence>
                  {showLangMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -5, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -5, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      style={{ position: 'absolute', right: 0, top: '100%' }}
                    >
                      <LangDropdown>
                        {languages.map((language) => (
                          <LangOption
                            key={language.code}
                            active={lang === language.code}
                            onClick={() => {
                              setLang(language.code)
                              setShowLangMenu(false)
                            }}
                          >
                            <span>{language.flag}</span>
                            <span>{language.name}</span>
                          </LangOption>
                        ))}
                      </LangDropdown>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            </HeaderInner>
          </Header>
        </motion.div>

        {/* Hero Section */}
        <HeroSection>
          <HeroBg>
            <img src={cryptoBackground.src} alt='' />
            <HeroBgGradient />
          </HeroBg>

          <HeroContent>
            <motion.div initial='initial' animate='animate'>
              <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
                <motion.div {...fadeInUp}>
                  <HeroTitle>
                    {t.nextLevel} <GradientText>{t.marketIntelligence}</GradientText>
                  </HeroTitle>
                </motion.div>
                <motion.div {...fadeInUp}>
                  <HeroSubtitle>{t.heroSubtitle}</HeroSubtitle>
                </motion.div>
              </Box>
            </motion.div>

            <ShimmerBorder>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
              >
                <VideoBox>
                  {playingVideo === 'intro' ? (
                    <video
                      src='https://s3.polar-tensor.io/polar/home.mp4'
                      controls
                      autoPlay
                      style={{ width: '100%', height: '100%' }}
                    />
                  ) : (
                    <VideoCover>
                      <VideoCoverImage
                        src={introCover.src}
                        alt='Company Introduction'
                      />
                      <VideoCoverOverlay>
                        <PlayBtn onClick={() => setPlayingVideo('intro')}>
                          <Play
                            size={24}
                            color='white'
                            fill='white'
                            style={{ marginLeft: 2 }}
                          />
                        </PlayBtn>
                      </VideoCoverOverlay>
                    </VideoCover>
                  )}
                </VideoBox>
              </motion.div>
            </ShimmerBorder>

            <motion.div
              initial={{ opacity: 0, y: 20, filter: 'blur(6px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.9, delay: 0.6, ease: premiumEase }}
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 24,
              }}
            >
              <CTAButton href={signUpUrl}>
                {t.startTradingNow}
                <ArrowRight size={18} />
              </CTAButton>
            </motion.div>
          </HeroContent>
        </HeroSection>

        {/* Felix Bick - Founder Section */}
        <FounderSection>
          <Container sx={{ maxWidth: 1024, px: { xs: 2, md: 3 } }}>
            <motion.div
              initial={{ opacity: 0, y: 50, filter: 'blur(12px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 1.1, ease: premiumEase }}
            >
              <FounderGrid>
                <FounderImageWrap>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, filter: 'blur(8px)' }}
                    whileInView={{
                      opacity: 1,
                      scale: 1,
                      filter: 'blur(0px)',
                    }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 1,
                      delay: 0.1,
                      ease: premiumEase,
                    }}
                  >
                    <FounderImage
                      src={felixBick.src}
                      alt='Felix Bick - Founder & CEO'
                    />
                  </motion.div>
                </FounderImageWrap>
                <FounderTextWrap>
                  <motion.div
                    initial={{ opacity: 0, y: 25, filter: 'blur(8px)' }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                      filter: 'blur(0px)',
                    }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.9,
                      delay: 0.2,
                      ease: premiumEase,
                    }}
                  >
                    <FounderName>Felix Bick</FounderName>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20, filter: 'blur(6px)' }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                      filter: 'blur(0px)',
                    }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.8,
                      delay: 0.25,
                      ease: premiumEase,
                    }}
                  >
                    <FounderRole>{t.founderCeo}</FounderRole>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20, filter: 'blur(6px)' }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                      filter: 'blur(0px)',
                    }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.9,
                      delay: 0.3,
                      ease: premiumEase,
                    }}
                  >
                    <FounderBio>{t.felixBio1}</FounderBio>
                    <FounderBio>{t.felixBio2}</FounderBio>
                    <FounderBio>{t.felixBio3}</FounderBio>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20, filter: 'blur(6px)' }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                      filter: 'blur(0px)',
                    }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.9,
                      delay: 0.4,
                      ease: premiumEase,
                    }}
                  >
                    <FounderQuoteBox>
                      <FounderQuote>{t.felixQuote}</FounderQuote>
                    </FounderQuoteBox>
                  </motion.div>
                </FounderTextWrap>
              </FounderGrid>
            </motion.div>
          </Container>
        </FounderSection>

        {/* Full Presentation Video Section */}
        <VideoSection bg='#0a1628'>
          <SectionBg>
            <img src={presentationBackground.src} alt='' />
            <SectionBgGradient base='rgba(10,22,40,0.6)' />
          </SectionBg>

          <SectionContent>
            <motion.div
              initial={{ opacity: 0, y: 50, filter: 'blur(12px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 1.1, ease: premiumEase }}
              style={{ textAlign: 'center', marginBottom: 24 }}
            >
              <SectionTitle>{t.fullPresentation}</SectionTitle>
              <SectionSubtitle>{t.presentationSubtitle}</SectionSubtitle>
            </motion.div>

            <motion.div
              initial={{
                opacity: 0,
                scale: 0.96,
                y: 50,
                filter: 'blur(10px)',
              }}
              whileInView={{
                opacity: 1,
                scale: 1,
                y: 0,
                filter: 'blur(0px)',
              }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{
                duration: 1.2,
                ease: premiumEase,
                delay: 0.15,
              }}
            >
              <CyanShimmerBorder>
                <VideoSectionBox>
                  {playingVideo === 'presentation' ? (
                    <video
                      src='https://s3.polar-tensor.io/polar/presentation_only.mp4'
                      controls
                      autoPlay
                      style={{ width: '100%', height: '100%' }}
                    />
                  ) : (
                    <VideoSectionCover>
                      <img src={financeBusinessImage.src} alt='Full Presentation' />
                      <VideoSectionOverlay>
                        <CyanPlayBtn onClick={() => setPlayingVideo('presentation')}>
                          <Play
                            size={24}
                            color='white'
                            fill='white'
                            style={{ marginLeft: 2 }}
                          />
                        </CyanPlayBtn>
                      </VideoSectionOverlay>
                    </VideoSectionCover>
                  )}
                </VideoSectionBox>
              </CyanShimmerBorder>
            </motion.div>
          </SectionContent>
        </VideoSection>

        {/* Polar One Software Video Section */}
        <VideoSection bg='#0d1117'>
          <SectionBg>
            <img src={softwareBackground.src} alt='' />
            <SectionBgGradient base='rgba(13,17,23,0.6)' />
          </SectionBg>

          <SectionContent>
            <motion.div
              initial={{ opacity: 0, y: 50, filter: 'blur(12px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 1.1, ease: premiumEase }}
              style={{ textAlign: 'center', marginBottom: 24 }}
            >
              <SectionTitle>{t.softwareTitle}</SectionTitle>
              <SectionSubtitle>{t.softwareSubtitle}</SectionSubtitle>
            </motion.div>

            <motion.div
              initial={{
                opacity: 0,
                scale: 0.96,
                y: 50,
                filter: 'blur(10px)',
              }}
              whileInView={{
                opacity: 1,
                scale: 1,
                y: 0,
                filter: 'blur(0px)',
              }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{
                duration: 1.2,
                ease: premiumEase,
                delay: 0.15,
              }}
            >
              <VioletShimmerBorder>
                <VideoSectionBox>
                  {playingVideo === 'software' ? (
                    <video
                      src='https://s3.polar-tensor.io/polar/software_video_english.mp4'
                      controls
                      autoPlay
                      style={{ width: '100%', height: '100%' }}
                    />
                  ) : (
                    <VideoSectionCover>
                      <img src={softwareScreenshot.src} alt='Polar One Software' />
                      <VideoSectionOverlay>
                        <CyanPlayBtn onClick={() => setPlayingVideo('software')}>
                          <Play
                            size={24}
                            color='white'
                            fill='white'
                            style={{ marginLeft: 2 }}
                          />
                        </CyanPlayBtn>
                      </VideoSectionOverlay>
                    </VideoSectionCover>
                  )}
                </VideoSectionBox>
              </VioletShimmerBorder>
            </motion.div>
          </SectionContent>
        </VideoSection>

        {/* Marketing & Rewards Plan Section */}
        <MarketingSection>
          <Container sx={{ maxWidth: 896, px: { xs: 2, md: 3 } }}>
            <motion.div
              initial={{ opacity: 0, y: 50, filter: 'blur(12px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 1.1, ease: premiumEase }}
            >
              <MarketingCard>
                <motion.div
                  initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                    filter: 'blur(0px)',
                  }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.9,
                    delay: 0.2,
                    ease: premiumEase,
                  }}
                >
                  <SectionTitle sx={{ mb: { xs: 1.5, md: 2 } }}>
                    {t.marketingTitle}
                  </SectionTitle>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 25, filter: 'blur(8px)' }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                    filter: 'blur(0px)',
                  }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.9,
                    delay: 0.3,
                    ease: premiumEase,
                  }}
                >
                  <SectionSubtitle sx={{ mb: { xs: 3, md: 4 } }}>
                    {t.marketingSubtitle}
                  </SectionSubtitle>
                </motion.div>
                <motion.div
                  initial={{
                    opacity: 0,
                    y: 25,
                    scale: 0.95,
                    filter: 'blur(6px)',
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    filter: 'blur(0px)',
                  }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.9,
                    delay: 0.4,
                    ease: premiumEase,
                  }}
                >
                  <GradientButton onClick={() => setShowRewardsPopup(true)}>
                    {t.watchNow}
                  </GradientButton>
                </motion.div>
              </MarketingCard>
            </motion.div>
          </Container>
        </MarketingSection>

        {/* CTA Section */}
        <CTASection>
          <Container
            sx={{
              maxWidth: 896,
              textAlign: 'center',
              px: { xs: 2, md: 3 },
              position: 'relative',
              zIndex: 10,
            }}
          >
            <motion.div
              initial={{
                opacity: 0,
                y: 60,
                scale: 0.95,
                filter: 'blur(15px)',
              }}
              whileInView={{
                opacity: 1,
                y: 0,
                scale: 1,
                filter: 'blur(0px)',
              }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 1.3, ease: premiumEase }}
            >
              <CTACard>
                <motion.div
                  initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                    filter: 'blur(0px)',
                  }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 1,
                    delay: 0.3,
                    ease: premiumEase,
                  }}
                >
                  <CTATitle>{t.ctaTitle}</CTATitle>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20, filter: 'blur(6px)' }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                    filter: 'blur(0px)',
                  }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.9,
                    delay: 0.5,
                    ease: premiumEase,
                  }}
                >
                  <GradientButton href={signUpUrl}>{t.enrollNow}</GradientButton>
                </motion.div>
              </CTACard>
            </motion.div>
          </Container>
        </CTASection>

        {/* Rewards Video Popup */}
        <AnimatePresence>
          {showRewardsPopup && (
            <PopupOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowRewardsPopup(false)}
            >
              <PopupContent
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <CloseBtn onClick={() => setShowRewardsPopup(false)}>
                  <X size={20} />
                </CloseBtn>
                <video
                  src='https://s3.polar-tensor.io/polar/referral_rewards_plan.mp4'
                  controls
                  autoPlay
                  style={{ width: '100%', height: '100%', border: 0 }}
                />
              </PopupContent>
            </PopupOverlay>
          )}
        </AnimatePresence>

        {/* Still Not Ready Section */}
        <NotReadySection>
          <Container
            sx={{
              maxWidth: 896,
              textAlign: 'center',
              px: { xs: 2, md: 3 },
              position: 'relative',
              zIndex: 10,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 50, filter: 'blur(12px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 1.1, ease: premiumEase }}
            >
              <NotReadyLabel>{t.stillNotReady}</NotReadyLabel>
              <NotReadyTitle>{t.shootMessage}</NotReadyTitle>
            </motion.div>
          </Container>
        </NotReadySection>

        {/* Advanced AI Section */}
        <AISection>
          <Container
            sx={{
              maxWidth: 1024,
              textAlign: 'center',
              px: { xs: 2, md: 3 },
              position: 'relative',
              zIndex: 10,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 35, filter: 'blur(10px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 1, ease: premiumEase }}
            >
              <AITitle>{t.advancedAi}</AITitle>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 35, filter: 'blur(10px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 1, delay: 0.2, ease: premiumEase }}
            >
              <AIQuote>&ldquo;{t.quoteText}&rdquo;</AIQuote>
            </motion.div>
          </Container>
        </AISection>

        {/* Risk Disclaimer Section */}
        <DisclaimerSection>
          <Container sx={{ maxWidth: 768, px: { xs: 2, md: 3 } }}>
            <motion.div
              initial={{ opacity: 0, y: 30, filter: 'blur(8px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.9, ease: premiumEase }}
            >
              <DisclaimerText>{t.riskDisclaimer}</DisclaimerText>
            </motion.div>
          </Container>
        </DisclaimerSection>
      </Box>
    </Root>
  )
}
